import { Injectable, inject, NgZone } from '@angular/core';
import { TranslateService, type TranslationObject } from '@ngx-translate/core';

/**
 * Translation Cheat Mode
 *
 * Hold Shift, then type: Up, Down, Left, Right, C, C, T, N
 * All translated text switches to show the translation key paths.
 * Resets on page refresh.
 */

const SEQUENCE = [
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'c',
  'c',
  't',
  'n',
];

const TIMEOUT_MS = 3000;

function generateKeyMap(
  obj: Record<string, unknown>,
  prefix = '',
): TranslationObject {
  const result: TranslationObject = {};
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = generateKeyMap(value as Record<string, unknown>, fullKey);
    } else {
      result[key] = fullKey;
    }
  }
  return result;
}

@Injectable({ providedIn: 'root' })
export class TranslationCheatService {
  readonly #translate = inject(TranslateService);
  readonly #zone = inject(NgZone);

  #progress = 0;
  #timeout: ReturnType<typeof setTimeout> | null = null;
  #badge: HTMLElement | null = null;

  constructor() {
    this.#zone.runOutsideAngular(() => {
      document.addEventListener('keydown', (e) => this.#onKeyDown(e));
    });
  }

  #onKeyDown(e: KeyboardEvent): void {
    // Ignore modifier keys themselves
    if (e.key === 'Shift') return;

    // Shift must be held for the entire sequence
    if (!e.shiftKey) {
      this.#progress = 0;
      this.#clearTimeout();
      return;
    }

    const expected = SEQUENCE[this.#progress];
    // Normalize: Shift turns 'c' into 'C', so compare lowercase
    const actual = e.key.length === 1 ? e.key.toLowerCase() : e.key;

    if (actual === expected) {
      this.#progress++;
      this.#resetTimeout();

      if (this.#progress === SEQUENCE.length) {
        this.#activate();
        this.#progress = 0;
      }
    } else {
      this.#progress = 0;
      this.#clearTimeout();
    }
  }

  #resetTimeout(): void {
    this.#clearTimeout();
    this.#timeout = setTimeout(() => {
      this.#progress = 0;
    }, TIMEOUT_MS);
  }

  #clearTimeout(): void {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
  }

  #activate(): void {
    this.#clearTimeout();
    const lang = this.#translate.getCurrentLang();

    // Access the store's translations via cast (store is private)
    const store = (
      this.#translate as unknown as {
        store: { getTranslations(lang: string): Record<string, unknown> };
      }
    ).store;
    const translations = store.getTranslations(lang);

    if (!translations) {
      console.warn('No translations loaded for', lang);
      return;
    }

    const keyMap = generateKeyMap(translations as Record<string, unknown>);

    this.#zone.run(() => {
      this.#translate.setTranslation(lang, keyMap, false);
    });

    this.#showBadge();
    console.info('Translation key mode activated');
  }

  #showBadge(): void {
    if (this.#badge) return;

    const el = document.createElement('div');
    el.textContent = 'KEY MODE';
    el.style.cssText = [
      'position:fixed',
      'bottom:8px',
      'right:8px',
      'z-index:99999',
      'padding:4px 8px',
      'font-size:10px',
      'font-family:monospace',
      'font-weight:bold',
      'color:#fff',
      'background:#e11d48',
      'border-radius:4px',
      'pointer-events:none',
      'opacity:0.9',
    ].join(';');

    document.body.appendChild(el);
    this.#badge = el;
  }
}
